import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../services/supabaseService';

// Tworzy rekord użytkownika w tabeli users jeśli nie istnieje.
// Aktualizuje avatar_url przy każdym logowaniu (np. z Google OAuth).
const ensureUserRecord = async (uid, email, avatarUrl) => {
  try {
    const { data: existing, error } = await supabase
      .from('users')
      .select('id')
      .eq('id', uid)
      .single();
    if (error && error.code !== 'PGRST116') return; // PGRST116 = no rows found
    if (!existing) {
      await supabase.from('users').insert({
        id: uid,
        email: email || '',
        avatar_url: avatarUrl || null,
        created_at: new Date().toISOString(),
        lastlogintime: new Date().toISOString(),
        is_active: true,
        favorites: [],
        favorite_tenants: [],
      });
    } else if (avatarUrl) {
      // Aktualizuj avatar przy każdym logowaniu (Google może zmienić zdjęcie)
      await supabase.from('users')
        .update({ avatar_url: avatarUrl, lastlogintime: new Date().toISOString() })
        .eq('id', uid);
    }
  } catch (err) {
  }
};

export const useAuth = () => {
  // Authentication state
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  
  // Modal states
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [loginIntent, setLoginIntent] = useState(null); // 'addListing' lub null
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  
  // Password recovery mode (from email link)
  // Wykryj SYNCHRONICZNIE z URL zanim Supabase wyczyści hash
  const [isRecoveryMode, setIsRecoveryMode] = useState(() => {
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    return hash.includes('type=recovery') || search.includes('type=recovery');
  });

  // Delete account dialogs
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteSuccessOpen, setDeleteSuccessOpen] = useState(false);
  
  // Favorites state
  const [favoriteProperties, setFavoriteProperties] = useState([]);
  const [favoritesCount, setFavoritesCount] = useState(0);

  // Wykryj sesję przy starcie (obsługuje powrót po OAuth redirect)
  useEffect(() => {
    // Sprawdź czy URL zawiera token recovery (z linku emailowego)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const urlParams = new URLSearchParams(window.location.search);
    const isRecoveryFromUrl = hashParams.get('type') === 'recovery' || urlParams.get('type') === 'recovery';
    
    // Sprawdź czy URL zawiera błąd auth (np. wygasły link)
    const hashError = hashParams.get('error_description');
    if (hashError) {
      // Pokaż alert z opisem błędu i wyczyść URL
      const errorMsg = decodeURIComponent(hashError.replace(/\+/g, ' '));
      setTimeout(() => {
        alert(`Link wygasł lub jest nieprawidłowy.\n\n${errorMsg}\n\nWyślij nowy link resetujący.`);
      }, 500);
      window.history.replaceState(null, '', window.location.pathname);
    }

    // Sprawdź aktualną sesję (np. po odświeżeniu strony lub OAuth redirect)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setIsUserLoggedIn(true);
        setUserEmail(session.user.email || '');
        setUserId(session.user.id);
        const avatarUrl = session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || null;
        ensureUserRecord(session.user.id, session.user.email, avatarUrl);
        // Jeśli URL wskazuje na recovery, pokaż dialog
        if (isRecoveryFromUrl) {
          setIsRecoveryMode(true);
          // Wyczyść hash/params z URL
          window.history.replaceState(null, '', window.location.pathname);
        }
      }
    });

    // Nasłuchuj na zmiany sesji (logowanie, wylogowanie, OAuth redirect)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setIsUserLoggedIn(true);
        setUserEmail(session.user.email || '');
        setUserId(session.user.id);
        // Dla nowych logowań (w tym Google OAuth) upewnij się, że rekord istnieje
        if (event === 'SIGNED_IN') {
          const avatarUrl = session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || null;
          ensureUserRecord(session.user.id, session.user.email, avatarUrl);
        }
      } else if (event === 'SIGNED_OUT') {
        setIsUserLoggedIn(false);
        setUserEmail('');
        setUserId(null);
        setFavoriteProperties([]);
        setFavoritesCount(0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Authentication success handler
  const handleAuthSuccess = useCallback((favorites, email, userIdFromModal) => {
    setIsUserLoggedIn(true);
    setUserEmail(email);
    setUserId(userIdFromModal);
  }, []);

  // Fetch favorite properties
  const fetchFavoriteProperties = useCallback(async () => {
    if (!userId) {
      return;
    }
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('favorites')
        .eq('id', userId)
        .single();
      
      if (error) throw error;
      
      const favoriteIds = userData?.favorites || [];
      if (favoriteIds.length === 0) {
        setFavoriteProperties([]);
        return;
      }
      
      const { data: properties, error: propError } = await supabase
        .from('properties')
        .select('*')
        .in('id', favoriteIds);
        
      if (propError) throw propError;
      setFavoriteProperties(properties || []);
    } catch (err) {
      setFavoriteProperties([]);
    }
  }, [userId]);

  // Delete account handler
  const handleDeleteAccount = useCallback(async (refreshCallbacks) => {
    try {
      
      // Soft delete konta użytkownika - oznacz jako nieaktywne
      const { error: userError } = await supabase
        .from('users')
        .update({ 
          is_active: false,
          deleted_at: new Date().toISOString()
        })
        .eq('id', userId);
      
      if (userError) throw userError;
      
      // HARD DELETE: Całkowicie usuń profile najemców użytkownika
      const { error: tenantError } = await supabase
        .from('tenant_profiles')
        .delete()
        .eq('user_id', userId);
      
      if (tenantError) {}
      
      // HARD DELETE: Całkowicie usuń ogłoszenia nieruchomości użytkownika
      
      const { error: propertiesError } = await supabase
        .from('properties')
        .delete()
        .eq('user_id', userId);
      
      if (propertiesError) {
      } else {
      }
      
      
      // Krótkie opóźnienie dla synchronizacji bazy danych
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Wyloguj użytkownika z auth
      await supabase.auth.signOut();
      
      // Wyczyść lokalny stan
      setIsUserLoggedIn(false);
      setUserEmail('');
      setUserId(null);
      setFavoriteProperties([]);
      setSettingsModalOpen(false);
      setDeleteConfirmOpen(false);
      
      // Odśwież listy na dashboardzie jeśli callback został podany
      if (refreshCallbacks) {
        try {
          if (refreshCallbacks.fetchInitialProperties) {
            await refreshCallbacks.fetchInitialProperties();
          }
          if (refreshCallbacks.fetchInitialTenants) {
            await refreshCallbacks.fetchInitialTenants();
          }
          if (refreshCallbacks.setRecentProperties) {
            refreshCallbacks.setRecentProperties([]);
          }
          if (refreshCallbacks.setRecentTenantProfiles) {
            refreshCallbacks.setRecentTenantProfiles([]);
          }
          if (refreshCallbacks.setVerifiedTenantProfiles) {
            refreshCallbacks.setVerifiedTenantProfiles([]);
          }
          if (refreshCallbacks.setPremiumTenantProfiles) {
            refreshCallbacks.setPremiumTenantProfiles([]);
          }
        } catch (refreshError) {
        }
      }
      
      // Wymuś wyczyszczenie stanów przed odświeżeniem
      setTimeout(() => {
        setDeleteSuccessOpen(true);
      }, 100);
      
    } catch (error) {
      alert('Wystąpił błąd podczas usuwania konta. Spróbuj ponownie.');
    }
  }, [userId]);

  // Logout handler
  const handleLogout = useCallback(async () => {
    try {
      // Natychmiastowo wyczyść stan (przed signOut, żeby UI się zaktualizował nawet jeśli signOut rzuci błąd)
      setIsUserLoggedIn(false);
      setUserEmail('');
      setUserId(null);
      setUserMenuAnchor(null);
      setFavoriteProperties([]);
      setFavoritesCount(0);
      
      // Wyloguj z Supabase (może nie istnieć sesja dla testowego użytkownika)
      try {
        await supabase.auth.signOut();
      } catch (e) {
      }
      
      
    } catch (error) {
    }
  }, []);

  return {
    // State
    isUserLoggedIn,
    userEmail,
    userId,
    userMenuAnchor,
    authModalOpen,
    loginIntent,
    settingsModalOpen,
    isRecoveryMode,
    deleteConfirmOpen,
    deleteSuccessOpen,
    favoriteProperties,
    favoritesCount,
    
    // Setters
    setIsUserLoggedIn,
    setUserEmail,
    setUserId,
    setUserMenuAnchor,
    setAuthModalOpen,
    setLoginIntent,
    setSettingsModalOpen,
    setIsRecoveryMode,
    setDeleteConfirmOpen,
    setDeleteSuccessOpen,
    setFavoriteProperties,
    setFavoritesCount,
    
    // Actions
    handleAuthSuccess,
    fetchFavoriteProperties,
    handleDeleteAccount,
    handleLogout
  };
};